import { Injectable } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { ExtraInfo, Order, PaymentMethod } from 'src/app/core/models/order';
import { BancontactTerminalReminderComponent } from '../modals/bancontact-terminal-reminder/bancontact-terminal-reminder/bancontact-terminal-reminder.component';
import { PassedOrderPreviewComponent } from '../modals/passed-order-preview/passed-order-preview.component';
import { NotificationType } from '../notification/notification';
import { PusherService } from '../notification/pusher.service';
import { CourierRepo } from '../repositories/courier.repo';
import { AccountService } from './account.service';
import { ToastPosition, ToastService } from './toast.service';
import { ExtraInfoReminderComponent } from '../modals/extra-info-reminder/extra-info-reminder.component';
import { VivaWalletPaymentService } from './vivawalet-payment.service';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private readonly orderRefreshEvents: NotificationType[] = [NotificationType.OrderAssigned, NotificationType.OrderAssignmentCleared, NotificationType.OrderCanceled, NotificationType.OrderPaid];
  private readonly orderPassOnEvents: NotificationType[] = [NotificationType.OrderAccepted, NotificationType.OrderDenied, NotificationType.OrderPushed];
  private _orders$: BehaviorSubject<Order[]> = new BehaviorSubject<Order[]>([]);
  private _finishedOrders$: BehaviorSubject<Order[]> = new BehaviorSubject<Order[]>([]);
  private orders: Order[] = [];
  private orderSequence?: string[];

  orders$: Observable<Order[]> = this._orders$.asObservable();
  finishedOrders$: Observable<Order[]> = this._finishedOrders$.asObservable();

  constructor(
    private courierRepo: CourierRepo,
    private pusherService: PusherService,
    private modalController: ModalController,
    private accountService: AccountService,
    private toastService: ToastService,
    private toastController: ToastController,
    private vivaPayment: VivaWalletPaymentService,
  ) {
    this.handleOrderRefreshEvents();
    this.handleOrderPassOnEvents();
    this.handleVivaPaymentEvents();
  }

  updateOrders(orders: Order[]): void {
    this.orders = orders;
    this.orderSequence = this.orders.map(order => order.id);
    this._orders$.next(orders);
  }

  updateFinishedOrders(finishedOrders: Order[]): void {
    this._finishedOrders$.next(finishedOrders);
  }

  hasOrdersInDelivery(): boolean {
    return this.orders.some(x => x.order.isInDelivery);
  }

  startDelivery(orderId: string): Observable<unknown> {
    const hasOrderWithCardPayment = this.orders.some(order => order.order.paymentType === PaymentMethod.Card && !order.order.paymentComplete);
    const orderToStart = this.orders.find(order => order.id === orderId)!;
    const showExtraInfoReminder = !!orderToStart.client.oneTimeExtraInfo;

    const reminder$ = hasOrderWithCardPayment ? of(this.showCardTerminalReminderDialog()) : of({});
    const extraInfoReminder$ = showExtraInfoReminder ? of(this.showExtraInfoReminderDialog(orderToStart.client.oneTimeExtraInfo!)) : of({});

    return reminder$.pipe(
      switchMap(() => extraInfoReminder$),
      switchMap(() => this.courierRepo.startDelivery(orderId)),
      switchMap(() => this.accountService.setReadyForDelivery(false)),
      tap(() => {
        orderToStart.order.isInDelivery = true;
        orderToStart.order.isReadyForDelivery = false;
      })
    );
  }

  endDelivery(orderId: string): Observable<unknown> {
    return this.courierRepo.endDelivery(orderId);
  }

  addNote(extraInfo: ExtraInfo): Observable<unknown> {
    return this.courierRepo.updateExtraInfo(extraInfo);
  }

  refreshOrders(): Observable<Order[]> {
    return this.courierRepo.getOrders().pipe(
      map((orders: Order[]) => this.orderBySequence(orders)),
      tap(orders => {
        this.updateOrders(orders.filter(order => !order.order.isFinished));
        this.updateFinishedOrders(orders.filter(order => order.order.isFinished));
      }),
    );
  }

  async presentPassedOrderPreviewModal(orderId: string): Promise<void> {
    this.courierRepo.getPushedOrders()
      .subscribe(async (orders) => {
        const order = orders.find(order => order.id === orderId);
        if (!order) {
          this.toastService.showWarningToast("Order wordt niet meer aangeboden", ToastPosition.Top);
          return;
        }

        const modal = await this.modalController.create({
          component: PassedOrderPreviewComponent,
          id: 'passed-order-preview',
          breakpoints: [0, 0.75],
          initialBreakpoint: 0.75,
          componentProps: {
            data: order
          }
        });
        await modal.present();
      })
  }

  private handleOrderRefreshEvents(): void {
    this.pusherService.onPusherEvent$
      .pipe(
        filter((event) => this.orderRefreshEvents.includes(event.type)),
        switchMap(() => this.refreshOrders())
      ).subscribe();
  }

  private handleOrderPassOnEvents(): void {
    this.pusherService.onPusherEvent$
      .pipe(
        filter((event) => this.orderPassOnEvents.includes(event.type)),
        tap((event) => {
          switch (event.type) {
            case NotificationType.OrderAccepted:
              this.toastService.showSuccessToast("Order sucessvol doorgegeven", ToastPosition.Top);
              this.refreshOrders().subscribe();
              break;
            case NotificationType.OrderDenied:
              this.toastService.showWarningToast("Order doorgegeven geweigerd", ToastPosition.Top);
              break;
            case NotificationType.OrderPushed:
              this.showOrderPushedToast(event.orderId);
              break;
          }
        })
      ).subscribe();
  }

  private handleVivaPaymentEvents() {
    this.vivaPayment.saleResult$
      .pipe(
        filter(r => r.status === 'success')
      )
      .subscribe(({ orderId }) => this.markPaid(orderId));
  }

  private orderBySequence(orders: Order[]): Order[] {
    // if there is an ordersequence present order by that sequence
    if (this.orderSequence) {
      orders.sort((a, b) =>
        this.orderSequence!.indexOf(a.id) - this.orderSequence!.indexOf(b.id)
      );
    } else {  // else order by distance in km
      orders.sort((a, b) => a.client.distanceInKm - b.client.distanceInKm);
    }
    // always put order that is in delivery first
    const orderInDeliveryIndex = orders.findIndex(order => order.order.isInDelivery);
    if (orderInDeliveryIndex != -1) {
      const orderInDelivery = orders[orderInDeliveryIndex];
      orders.splice(orderInDeliveryIndex, 1);
      orders = [orderInDelivery, ...orders];
    }
    return orders;
  }

  private async showCardTerminalReminderDialog(): Promise<void> {
    const modal = await this.modalController.create({
      component: BancontactTerminalReminderComponent,
      cssClass: 'bancontact-terminal-reminder-modal-css',
    });
    await modal.present();
  }

  private async showExtraInfoReminderDialog(oneTimeInfo: string): Promise<void> {
    const modal = await this.modalController.create({
      component: ExtraInfoReminderComponent,
      cssClass: 'extra-info-reminder-modal-css',
      componentProps: {
        extraOneTimeInfo: oneTimeInfo,
      }
    });
    await modal.present();
  }

  private markPaid(orderId: string): void {
    const idx = this.orders.findIndex(o => o.id === orderId);
    if (idx > -1) {
      this.orders[idx].order.paymentComplete = true;
      this._orders$.next([...this.orders]);
    }
  }

  private async showOrderPushedToast(orderId: string): Promise<void> {
    this.toastController.create({
      header: 'Levering aan jou aangeboden!',
      position: 'top',
      duration: 5_000,
      color: 'primary',
      buttons: [
        {
          side: 'end',
          text: 'Order bekijken',
          handler: async () => {
            await this.presentPassedOrderPreviewModal(orderId)
          }
        }
      ]
    }).then((toast) => {
      toast.present();
    });
  }
}
